/**
 * App store URLs for download/scan pages.
 * Uses env vars with safe fallbacks. Helpers preserve UTM and src params.
 */

const UTM_AND_SRC_PARAMS = [
  "src",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

const FALLBACK_IOS = "https://apps.apple.com/app/id6758612852";
const FALLBACK_ANDROID =
  "https://play.google.com/store/apps/details?id=com.menuhunter.consumer";

export function getAppStoreUrls(): { ios: string; android: string } {
  const ios =
    typeof process.env.NEXT_PUBLIC_IOS_APP_STORE_URL === "string" &&
    process.env.NEXT_PUBLIC_IOS_APP_STORE_URL.length > 0
      ? process.env.NEXT_PUBLIC_IOS_APP_STORE_URL
      : FALLBACK_IOS;
  const android =
    typeof process.env.NEXT_PUBLIC_ANDROID_PLAY_STORE_URL === "string" &&
    process.env.NEXT_PUBLIC_ANDROID_PLAY_STORE_URL.length > 0
      ? process.env.NEXT_PUBLIC_ANDROID_PLAY_STORE_URL
      : FALLBACK_ANDROID;
  return { ios, android };
}

export type DeviceType = "ios" | "android" | "unknown";

/** Single store URL for the given device; desktop/unknown returns iOS URL as default. */
export function getAppStoreUrlForDevice(device: DeviceType): string {
  const { ios, android } = getAppStoreUrls();
  if (device === "android") return android;
  return ios;
}

/**
 * Append src and utm_* params from incoming URL search params to a target URL.
 * Safe to call with null/undefined searchParams (returns url unchanged).
 */
export function appendQueryParams(
  url: string,
  searchParams: URLSearchParams | ReadonlyURLSearchParams | null | undefined
): string {
  if (!searchParams) return url;
  try {
    const parsed = new URL(url);
    for (const key of UTM_AND_SRC_PARAMS) {
      const value = searchParams.get(key);
      if (value) parsed.searchParams.set(key, value);
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

type ReadonlyURLSearchParams = {
  get(name: string): string | null;
};
